import classNames from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import ReactCalendar from 'react-calendar';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import FloatingTooltip from '@/components/molecules/floating-tooltip/floating-tooltip';
import variables from '@/components/organisms/calendar/calendar.variables';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import { getRangeEndDate } from '@/libraries/getters/get-range-end-date';
import CalendarButton from './calendar-button/calendar-button';
import CalendarCharterIcon from './calendar-charter-icon/calendar-charter-icon';
import CalendarOfferIcon from './calendar-offer-icon/calendar-offer-icon';
import type { CalendarDateAnnotation } from './calendar.types';
import styles from './calendar.module.scss';

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

export interface CalendarProps {
  /** A list of dates that contain charter, marked with a dot */
  charterDates?: Date[];
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** A list of date annotations rendered as tooltips with custom tile styling */
  dateAnnotation?: CalendarDateAnnotation;
  /** Indicates the number of months to be displayed */
  displayedMonths?: number;
  /** Shows the first selectable month while nothing is selected (range) */
  focusFirstSelectableMonth?: boolean;
  /** A list of available dates that are highlighted in the calendar */
  highlightedDates?: Date[];
  /** Indicates whether the dropdown is in a loading state */
  isLoading?: boolean;
  /** Locale for the calendar labels */
  locale?: Locale;
  /** A list of low price dates to highlight in the calendar */
  lowPriceDates?: Date[];
  /** Maximum valid date for range */
  maxDate?: Date;
  /** Maximum number of days (both included) that a selected range can span */
  maxRangeDays?: number;
  /** Minimum valid date for range */
  minDate?: Date;
  /** A list of offers to show in the calendar */
  offers?: Date[];
  /** An event that is triggered on change */
  onChange?: (selectedDates: Date[]) => void;
  /** Indicates whether user can select a date range */
  rangeSelect?: boolean;
  /** Number of days that represents the length of the stay */
  rangeSize?: number;
  /**
   * Range select only: travel durations per departure date (YYYY-MM-DD). The departure dates are highlighted, and once
   * one is picked its possible return dates are highlighted instead
   */
  returnDurations?: Record<string, number[]>;
  /** Preselected date in the calendar */
  selectedDate?: Date | null;
  /** Preselected date range end in the calendar */
  selectedMaxDate?: Date | null;
}

const Calendar = ({
  charterDates = [],
  className,
  customRangeStyle = false,
  dateAnnotation = variables.dateAnnotation,
  displayedMonths = 2,
  focusFirstSelectableMonth = false,
  highlightedDates = [],
  isLoading = false,
  locale = 'en',
  lowPriceDates = [],
  maxDate,
  maxRangeDays,
  minDate,
  offers = [],
  onChange,
  rangeSelect = false,
  rangeSize = 7,
  returnDurations,
  selectedDate = null,
  selectedMaxDate = null,
}: CalendarProps) => {
  const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(null);
  // First date clicked of a range that is not completed yet
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const { t } = useI18n();
  const { dateFormatter } = useDateFormatter();

  const isDateHighlighted = (date: Date) => {
    return highlightedDates.some(
      highlightedDate => date.toDateString() === highlightedDate.toDateString(),
    );
  };

  // Departure dates before the first click, the return dates of the picked departure date after it
  const returnIndicatorDates = useMemo(() => {
    if (!rangeSelect || !returnDurations) return new Set<string>();

    if (!rangeStart) return new Set(Object.keys(returnDurations));

    const durations =
      returnDurations[dayjs(rangeStart).format('YYYY-MM-DD')] ?? [];

    return new Set(
      durations.map(duration =>
        dayjs(rangeStart).add(duration, 'day').format('YYYY-MM-DD'),
      ),
    );
  }, [rangeSelect, rangeStart, returnDurations]);

  const isReturnIndicatorDate = (date: Date) =>
    returnIndicatorDates.has(dayjs(date).format('YYYY-MM-DD'));

  // The months can only be browsed up to the last date that can be picked:
  // the last departure date, or once one is picked, its last return date.
  // A completed range can still be browsed up to its return date
  const lastSelectableDate = useMemo(() => {
    if (returnIndicatorDates.size === 0) return maxDate;

    // The dates are formatted as YYYY-MM-DD, so they sort chronologically
    const [lastDate] = Array.from(returnIndicatorDates).sort().reverse();
    const lastIndicatorDate = dayjs(lastDate).endOf('day').toDate();
    const selectedEndDate = !rangeStart ? selectedRange?.[1] : undefined;
    const lastDateToBrowse =
      selectedEndDate && selectedEndDate > lastIndicatorDate
        ? dayjs(selectedEndDate).endOf('day').toDate()
        : lastIndicatorDate;

    return maxDate && maxDate < lastDateToBrowse ? maxDate : lastDateToBrowse;
  }, [maxDate, rangeStart, returnIndicatorDates, selectedRange]);

  const handleClickDay = (date: Date) => {
    if (!rangeSelect) return;

    // The first click starts a range, the second one completes it
    setRangeStart(previousStart => (previousStart ? null : date));
  };

  const isDateLowPrice = (date: Date) => {
    return lowPriceDates.some(
      lowPriceDate => date.toDateString() === lowPriceDate.toDateString(),
    );
  };

  const isDateCharter = (date: Date) => {
    return charterDates.some(
      charterDate => date.toDateString() === charterDate.toDateString(),
    );
  };

  const handleDateChange = (value: Value) => {
    if (rangeSelect && Array.isArray(value) && value.length === 2) {
      setSelectedRange(value as [Date, Date]);
      onChange && onChange(value as [Date, Date]);
    } else if (!rangeSelect && value instanceof Date) {
      if (!isDateHighlighted(value)) return;

      onChange && onChange([value]);
    }
  };

  const isOutsideMaxRange = (date: Date): boolean => {
    if (!rangeStart || !maxRangeDays) return false;

    // Rounded, as a day across a daylight saving change is not 24 hours
    const days = Math.round(
      Math.abs(dayjs(date).diff(rangeStart, 'day', true)),
    );

    return days >= maxRangeDays;
  };

  const tileDisabled = ({ date }: { date: Date }): boolean => {
    if (rangeSelect) {
      if (
        (minDate && date < minDate) ||
        (maxDate && date > maxDate) ||
        isOutsideMaxRange(date)
      ) {
        return true;
      }

      // With travel durations, a range can only start on a departure date and
      // end on one of its return dates, the dates that are highlighted. Empty
      // travel durations mean there is no availability, so no date can be picked
      return returnDurations !== undefined && !isReturnIndicatorDate(date);
    }

    return !isDateHighlighted(date);
  };

  const hasAnnotationForDate = (date: Date): boolean => {
    const { endDate, startDate } = dateAnnotation?.range ?? {};

    if (!startDate || !endDate) return false;

    const day = new Date(date);

    day.setHours(0, 0, 0, 0);

    const start = new Date(startDate);

    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);

    end.setHours(0, 0, 0, 0);

    return day >= start && day <= end;
  };

  useEffect(() => {
    if (!rangeSelect) setRangeStart(null);
  }, [rangeSelect]);

  useEffect(() => {
    if (selectedDate === null) {
      setSelectedRange(null);
      setRangeStart(null);

      return;
    }

    if (!rangeSelect) {
      const endDate = getRangeEndDate(selectedDate, rangeSize + 1);

      setSelectedRange([selectedDate, endDate]);
    } else if (selectedDate && selectedMaxDate) {
      // A day only shows as picked when the range covers all of it, so the
      // range ends at the end of its last day, as a picked range does
      setSelectedRange([
        selectedDate,
        dayjs(selectedMaxDate).endOf('day').toDate(),
      ]);
    }
  }, [rangeSelect, rangeSize, selectedDate, selectedMaxDate]);

  const defaultActiveStartDate = selectedDate
    ? selectedDate
    : !rangeSelect && highlightedDates.length > 0
      ? highlightedDates[0]
      : rangeSelect && minDate
        ? minDate
        : undefined;

  // Follows the minimum date while nothing is selected
  const followsMinDate = focusFirstSelectableMonth && rangeSelect;
  const [rangeActiveStartDate, setRangeActiveStartDate] = useState<Date>();
  const minDateMonth = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), 1).getTime()
    : undefined;

  useEffect(() => {
    if (!followsMinDate || selectedDate || minDateMonth === undefined) return;

    setRangeActiveStartDate(new Date(minDateMonth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followsMinDate, minDateMonth]);

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasCustomRangeStyle]: customRangeStyle,
      })}
    >
      {isLoading && (
        <Spinner
          className={styles.spinner}
          loadingText={t('items.search.searchBar.updatingSearch')}
        />
      )}

      <ReactCalendar
        activeStartDate={followsMinDate ? rangeActiveStartDate : undefined}
        className={styles.calendar}
        defaultActiveStartDate={defaultActiveStartDate}
        formatDay={(_, date) => dateFormatter(date).format('D')}
        formatMonthYear={(_, date) =>
          capitalizeFirstLetter(dateFormatter(date).format('MMMM YYYY'))
        }
        formatShortWeekday={(_, date) =>
          capitalizeFirstLetter(dateFormatter(date).format('dd'))
        }
        locale={locale}
        maxDate={rangeSelect ? lastSelectableDate : undefined}
        maxDetail="month"
        minDate={rangeSelect ? minDate : undefined}
        minDetail="month"
        next2Label={null}
        nextAriaLabel={t('items.search.searchBar.date.nextMonth')}
        nextLabel={
          <CalendarButton
            aria-label={t('items.search.searchBar.date.nextMonth')}
            icon="ChevronRight"
          />
        }
        onActiveStartDateChange={
          followsMinDate
            ? ({ activeStartDate }) =>
                setRangeActiveStartDate(activeStartDate ?? undefined)
            : undefined
        }
        onChange={handleDateChange}
        onClickDay={handleClickDay}
        prev2Label={null}
        prevAriaLabel={t('items.search.searchBar.date.previousMonth')}
        prevLabel={
          <CalendarButton
            aria-label={t('items.search.searchBar.date.previousMonth')}
            icon="ChevronLeft"
          />
        }
        returnValue={rangeSelect ? 'range' : 'start'}
        selectRange={rangeSelect}
        showDoubleView={displayedMonths === 2}
        showFixedNumberOfWeeks={false}
        showWeekNumbers
        tileClassName={({ date }) => {
          const hasAnnotation = hasAnnotationForDate(date);

          return classNames({
            [styles.isAfterLastSelectable]:
              rangeSelect && !!lastSelectableDate && date > lastSelectableDate,
            [styles.isHighlighted]:
              isDateHighlighted(date) || isReturnIndicatorDate(date),
            ...(hasAnnotation && dateAnnotation?.styleModifier
              ? { [dateAnnotation.styleModifier]: true }
              : {}),
          });
        }}
        tileContent={({ date }) => {
          const hasLowPrice = isDateLowPrice(date);
          const hasOffer = offers?.find(
            offer =>
              dayjs(offer).format('YYYY-MM-DD') ===
              dayjs(date).format('YYYY-MM-DD'),
          );
          const hasAnnotation = hasAnnotationForDate(date);

          return (
            <div className={styles.tileContent}>
              {hasOffer && (
                <CalendarOfferIcon
                  className={styles.offerIndicator}
                  label={t('items.common.words.campaignDeals')}
                />
              )}

              {hasLowPrice && (
                <span
                  aria-label={t('items.common.words.cheapestDepartureDates')}
                  className={styles.lowPriceIndicator}
                  role="img"
                />
              )}

              {isDateCharter(date) && (
                <CalendarCharterIcon
                  className={styles.charterIndicator}
                  label={t('items.common.words.containsCharter')}
                />
              )}

              {hasAnnotation && dateAnnotation && (
                <FloatingTooltip
                  className={styles.annotationTrigger}
                  headline={dateAnnotation.tooltipHeadline}
                  maxWidth={300}
                  text={dateAnnotation.tooltipText}
                />
              )}
            </div>
          );
        }}
        tileDisabled={tileDisabled}
        value={selectedRange}
        view="month"
      />

      {(lowPriceDates.length > 0 ||
        offers.length > 0 ||
        charterDates.length > 0) && (
        <div className={styles.legend}>
          {charterDates.length > 0 && (
            <div className={styles.legendItem}>
              <CalendarCharterIcon size={16} />

              <Typography tagStyle="bodySmall">
                {t('items.common.words.containsCharter')}
              </Typography>
            </div>
          )}

          {offers.length > 0 && (
            <div className={styles.legendItem}>
              <CalendarOfferIcon />

              <Typography tagStyle="bodySmall">
                {t('items.common.words.campaignDeals')}
              </Typography>
            </div>
          )}

          {lowPriceDates.length > 0 &&
            t('items.common.words.cheapestDepartureDates') && (
              <div className={styles.legendItem}>
                <span
                  aria-hidden
                  className={classNames(styles.legendItemIcon)}
                />

                <Typography tagStyle="bodySmall">
                  {t('items.common.words.cheapestDepartureDates')}
                </Typography>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

Calendar.displayName = 'Calendar';

export default Calendar;
